import { Fragment, useCallback, useEffect, useState } from "react";
import { Link, Navigate, Route, Routes, useParams } from "react-router";
import { Button, Card, Checkbox, Cluster, EmptyState, FormField, Input, Select, Stack, Tabs, useToast } from "@salesapp/ui";
import { reportSaveFailure, reportSaved } from "../../lib/saveNotice";
import { useAuth } from "../../shell/AuthProvider";
import { useAccess } from "../../shell/AccessProvider";
import { useLocale } from "../../shell/LocaleProvider";
import { SectionNav } from "../../shell/SectionNav";
import { CapabilityGate } from "../crm/CapabilityGate";
import { appUrl } from "../../lib/customerUrl";
import "../crm/crm.css";
import "../pages.css";
import "../workforce.css";
import {
  workforceApi,
  type ExpenseCategory,
  type ExpenseLine,
  type ExpenseReport,
  type ExpenseSettings,
} from "../../lib/workforceApi";
import { EXPENSE_TYPES, asNumber, statusBadge, statusLabel, todayIso } from "../workforceUi";

function fail(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

export function ExpenseApp() {
  const { session } = useAuth();
  const orgId = session?.orgId ?? "";
  if (!orgId) return <Navigate to="/dashboard" replace />;
  const userId = session?.userId ?? "";
  const href = (path: string) => appUrl("expense", session?.orgSlug, path);

  return (
    <CapabilityGate capability="expense.reports">
      <div className="crm-app">
        <SectionNav
          label="Expense sections"
          items={[
            { to: href("/dashboards"), label: "Dashboards" },
            { to: href("/claims"), label: "Expenses and Claims" },
            { to: href("/submitted"), label: "Submitted reports" },
            { to: href("/categories"), label: "Categories" },
            { to: href("/settings"), label: "Settings" },
          ]}
        />
        <div className="crm-canvas">
          <Routes>
            <Route path="/" element={<Navigate to={href("/claims")} replace />} />
            <Route path="/dashboards" element={<ExpenseDashboard orgId={orgId} userId={userId} />} />
            <Route path="/claims" element={<ExpenseClaims orgId={orgId} userId={userId} reportHref={(id) => href(`/reports/${id}`)} />} />
            <Route path="/reports/:reportId" element={<ExpenseDetail orgId={orgId} />} />
            <Route path="/submitted" element={<ExpenseSubmitted orgId={orgId} userId={userId} reportHref={(id) => href(`/reports/${id}`)} />} />
            <Route path="/categories" element={<ExpenseCategories orgId={orgId} />} />
            <Route path="/settings" element={<ExpenseSettingsPage orgId={orgId} />} />
            <Route path="*" element={<Navigate to={href("/claims")} replace />} />
          </Routes>
        </div>
      </div>
    </CapabilityGate>
  );
}

function ExpenseDashboard({ orgId, userId }: { orgId: string; userId: string }) {
  const [reports, setReports] = useState<ExpenseReport[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void workforceApi.listExpenses(orgId).then((result) => setReports(result.reports)).catch((err) => setError(fail(err, "Could not load expenses.")));
  }, [orgId]);
  const visible = reports.filter((report) => report.user_id === userId || report.status !== "draft");
  const spend = visible.reduce((sum, report) => sum + asNumber(report.total), 0);
  const pending = reports.filter((report) => report.status === "submitted");
  const companyOwes = reports.filter((report) => report.status === "approved").reduce((sum, report) => sum + asNumber(report.reimbursable_total), 0);
  const flagged = reports.flatMap((report) => report.lines).filter((line) => line.missing_receipt).length;
  return (
    <Stack gap={4}>
      <div>
        <h1 className="home-title">Dashboards</h1>
        <p className="home-subtitle">Spend, pending approvals, and what the company owes employees.</p>
      </div>
      {error && <p className="crm-error">{error}</p>}
      <div className="home-kpi-grid">
        <Card><div className="home-kpi-label">Total spend</div><div className="home-kpi-value">{spend.toFixed(2)}</div></Card>
        <Card><div className="home-kpi-label">Pending approvals</div><div className="home-kpi-value">{pending.length}</div></Card>
        <Card><div className="home-kpi-label">Company owes employees</div><div className="home-kpi-value">{companyOwes.toFixed(2)}</div></Card>
        <Card><div className="home-kpi-label">Missing receipts</div><div className="home-kpi-value">{flagged}</div></Card>
      </div>
    </Stack>
  );
}

function emptyLine(category: string, spentOn: string): ExpenseLine {
  return { spent_on: spentOn, category, amount: "", expense_type: "Expense", employee_paid: true };
}

const RECEIPT_ACCEPT = "application/pdf,image/png,image/jpeg,image/webp";
const RECEIPT_MAX_BYTES = 5 * 1024 * 1024;

/** Client-side pre-check; the API re-validates the file's real type and size. */
function receiptProblem(file: File): string | null {
  if (file.size === 0) return "That file is empty.";
  if (file.size > RECEIPT_MAX_BYTES) return "Receipts can be at most 5 MB.";
  if (!RECEIPT_ACCEPT.split(",").includes(file.type)) return "Receipts must be a PDF, PNG, JPEG or WebP file.";
  return null;
}

function lineBody(item: ExpenseLine): ExpenseLine {
  const next: ExpenseLine = { ...item, amount: Number(item.amount) };
  delete next.id;
  // Server-managed receipt file metadata is read-only; the API rejects unknown fields.
  delete next.receipt_filename;
  delete next.receipt_content_type;
  delete next.receipt_size;
  return next;
}

function ExpenseClaims({ orgId, userId, reportHref }: { orgId: string; userId: string; reportHref: (id: string) => string }) {
  const { show } = useToast();
  const { currency: userCurrency, timezone, formatMoney, formatDate } = useLocale();
  const today = todayIso(timezone);
  const [reports, setReports] = useState<ExpenseReport[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [settings, setSettings] = useState<ExpenseSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [periodStart, setPeriodStart] = useState(today);
  const [periodEnd, setPeriodEnd] = useState(today);
  const [active, setActive] = useState<ExpenseReport | null>(null);
  const [mode, setMode] = useState<"single" | "grid" | "mileage">("single");
  const [showLine, setShowLine] = useState(false);
  const [search, setSearch] = useState("");
  const [line, setLine] = useState<ExpenseLine>(emptyLine("Meals", today));
  const [grid, setGrid] = useState<ExpenseLine[]>([emptyLine("Meals", today)]);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [mileage, setMileage] = useState({ destination: "", per_diem_rate: "", per_diem_days: "", mileage_km: "", mileage_rate: "", comments: "" });

  const load = useCallback(async () => {
    try {
      const [reportResult, categoryResult, settingResult] = await Promise.all([
        workforceApi.listExpenses(orgId),
        workforceApi.listCategories(orgId),
        workforceApi.getSettings(orgId),
      ]);
      setReports(reportResult.reports);
      setCategories(categoryResult.categories);
      setSettings(settingResult);
      setActive((current) => current ? reportResult.reports.find((item) => item.id === current.id) ?? null : current);
    } catch (err) {
      setError(fail(err, "Expense reports could not be loaded."));
    }
  }, [orgId]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (settings?.default_mileage_rate && !mileage.mileage_rate) {
      setMileage((current) => ({ ...current, mileage_rate: settings.default_mileage_rate ?? "" }));
    }
  }, [settings, mileage.mileage_rate]);

  const mine = reports.filter(
    (report) => (report.user_id ?? "").toLowerCase() === (userId ?? "").toLowerCase(),
  );
  const filtered = mine.filter((report) => {
    const hay = `${report.title} ${report.description ?? ""} ${report.claim_number ?? ""}`.toLowerCase();
    return hay.includes(search.toLowerCase());
  });
  const drafts = filtered.filter((report) => report.status === "draft" || report.status === "rejected");
  const claims = filtered.filter((report) => report.status === "submitted" || report.status === "approved");
  const defaultCategory = categories[0]?.name ?? "Other Expenses";
  const mileageCost = (Number(mileage.mileage_km) || 0) * (Number(mileage.mileage_rate) || 0) + (Number(mileage.per_diem_rate) || 0) * (Number(mileage.per_diem_days) || 0);
  const editable = Boolean(active && ["draft", "rejected"].includes(active.status) && active.user_id === userId);

  async function run(action: () => Promise<unknown>, fallback: string, savedMessage?: string) {
    setError(null);
    try {
      await action();
      if (savedMessage) reportSaved(show, savedMessage);
      await load();
    } catch (err) {
      setError(reportSaveFailure(show, err, fallback));
    }
  }

  async function viewReceipt(reportId: string, lineId: string) {
    setError(null);
    try {
      const link = await workforceApi.getExpenseReceipt(orgId, reportId, lineId);
      window.open(link.url, "_blank", "noopener");
    } catch (err) {
      setError(fail(err, "The receipt could not be opened."));
    }
  }

  function pickReceipt(file: File | null) {
    if (!file) {
      setReceiptFile(null);
      return;
    }
    const problem = receiptProblem(file);
    if (problem) {
      setError(problem);
      setReceiptFile(null);
      return;
    }
    setError(null);
    setReceiptFile(file);
  }

  function hydrateReport(report: ExpenseReport) {
    setTitle(report.title);
    setDescription(report.description ?? "");
    setPeriodStart(report.period_start ?? today);
    setPeriodEnd(report.period_end ?? today);
    setShowLine(false);
    setEditingLineId(null);
    setReceiptFile(null);
    setLine(emptyLine(defaultCategory, today));
  }

  function startCreate() {
    setActive(null);
    setCreating(true);
    setTitle("");
    setDescription("");
    setPeriodStart(today);
    setPeriodEnd(today);
    setShowLine(false);
    setEditingLineId(null);
  }

  function openReport(report: ExpenseReport) {
    if (active?.id === report.id) {
      setActive(null);
      return;
    }
    setCreating(false);
    setActive(report);
    hydrateReport(report);
  }

  const headerFields = (
    <Stack gap={3}>
      <FormField label="Report name" htmlFor="ex-title" required>
        <Input id="ex-title" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Report name" disabled={Boolean(active) && !editable} />
      </FormField>
      <FormField label="Description" htmlFor="ex-desc">
        <Input id="ex-desc" value={description} onChange={(event) => setDescription(event.target.value)} disabled={Boolean(active) && !editable} />
      </FormField>
      <Cluster gap={3}>
        <FormField label="Start date" htmlFor="ex-start">
          <Input id="ex-start" type="date" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} disabled={Boolean(active) && !editable} />
        </FormField>
        <FormField label="End date" htmlFor="ex-end">
          <Input id="ex-end" type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} disabled={Boolean(active) && !editable} />
        </FormField>
      </Cluster>
    </Stack>
  );

  const lineCapture = active && showLine && editable ? (
    <Stack gap={3}>
      <Tabs
        items={[{ key: "single", label: "Single entry" }, { key: "grid", label: "Line items grid" }, { key: "mileage", label: "Mileage" }]}
        activeKey={mode}
        onChange={(key) => setMode(key as typeof mode)}
      />
      {mode === "single" && (
        <Stack gap={3}>
          <Cluster gap={3}>
            <FormField label="Transaction date" htmlFor="ln-date"><Input id="ln-date" type="date" value={line.spent_on} onChange={(event) => setLine({ ...line, spent_on: event.target.value })} /></FormField>
            <FormField label="Type" htmlFor="ln-type">
              <Select id="ln-type" value={line.expense_type ?? "Expense"} onChange={(event) => setLine({ ...line, expense_type: event.target.value })}>
                {EXPENSE_TYPES.map((item) => <option key={item}>{item}</option>)}
              </Select>
            </FormField>
            <FormField label="Category" htmlFor="ln-cat">
              <Select id="ln-cat" value={line.category} onChange={(event) => setLine({ ...line, category: event.target.value })}>
                {categories.map((item) => <option key={item.id}>{item.name}</option>)}
              </Select>
            </FormField>
            <FormField label="Amount" htmlFor="ln-amt"><Input id="ln-amt" value={String(line.amount ?? "")} onChange={(event) => setLine({ ...line, amount: event.target.value })} /></FormField>
          </Cluster>
          <Cluster gap={3}>
            <FormField label="Vendor" htmlFor="ln-vendor"><Input id="ln-vendor" value={line.vendor ?? ""} onChange={(event) => setLine({ ...line, vendor: event.target.value })} /></FormField>
            <FormField label="Description" htmlFor="ln-notes"><Input id="ln-notes" value={line.notes ?? ""} onChange={(event) => setLine({ ...line, notes: event.target.value })} /></FormField>
            <FormField label="Receipt note" htmlFor="ln-receipt"><Input id="ln-receipt" value={line.receipt_key ?? ""} onChange={(event) => setLine({ ...line, receipt_key: event.target.value })} /></FormField>
          </Cluster>
          <FormField label="Receipt file (PDF, PNG, JPEG or WebP, up to 5 MB)" htmlFor="ln-receipt-file">
            <input
              id="ln-receipt-file"
              type="file"
              accept={RECEIPT_ACCEPT}
              onChange={(event) => { pickReceipt(event.target.files?.[0] ?? null); event.target.value = ""; }}
            />
          </FormField>
          {receiptFile && <p className="wf-muted">Will upload: {receiptFile.name} <Button variant="ghost" onClick={() => setReceiptFile(null)}>Clear</Button></p>}
          {editingLineId && line.receipt_filename && !receiptFile && (
            <Cluster gap={2}>
              <span className="wf-muted">Attached: {line.receipt_filename}</span>
              <Button variant="ghost" onClick={() => viewReceipt(active.id, editingLineId)}>View</Button>
              <Button
                variant="ghost"
                onClick={() => run(async () => {
                  await workforceApi.deleteExpenseReceipt(orgId, active.id, editingLineId);
                  setLine({ ...line, receipt_filename: null, receipt_content_type: null, receipt_size: null });
                }, "The receipt could not be removed.")}
              >
                Remove file
              </Button>
            </Cluster>
          )}
          <Cluster gap={3}>
            <Checkbox label="Employee paid (refundable)" checked={Boolean(line.employee_paid)} onChange={(event) => setLine({ ...line, employee_paid: event.target.checked })} />
            <Checkbox label="Missing receipt" checked={Boolean(line.missing_receipt)} onChange={(event) => setLine({ ...line, missing_receipt: event.target.checked })} />
          </Cluster>
          <Cluster gap={2}>
            <Button
              onClick={() => run(async () => {
                const body = lineBody(line);
                let savedLineId = editingLineId;
                if (editingLineId) {
                  await workforceApi.patchExpenseLine(orgId, active.id, editingLineId, body);
                } else {
                  const before = new Set(active.lines.map((item) => item.id));
                  const updated = await workforceApi.addExpenseLine(orgId, active.id, body);
                  savedLineId = updated.lines.find((item) => item.id && !before.has(item.id))?.id ?? null;
                }
                if (receiptFile && savedLineId) {
                  try {
                    await workforceApi.uploadExpenseReceipt(orgId, active.id, savedLineId, receiptFile);
                  } catch (err) {
                    // The line is saved; say plainly that only the file failed.
                    throw new Error(`The line was saved but the receipt file was not: ${fail(err, "upload failed")}`);
                  }
                }
                setReceiptFile(null);
                setEditingLineId(null);
                setLine(emptyLine(defaultCategory, today));
                setShowLine(false);
              }, "Line could not be saved.", "Saved.")}
              disabled={!line.spent_on || !line.amount}
            >
              {editingLineId ? "Save line" : "Save line item"}
            </Button>
            <Button variant="ghost" onClick={() => { setShowLine(false); setEditingLineId(null); setReceiptFile(null); }}>Cancel</Button>
          </Cluster>
        </Stack>
      )}
      {mode === "grid" && (
        <Stack gap={3}>
          <p className="wf-muted">Use Single entry to attach a receipt file. Blank amount rows are skipped.</p>
          <table className="crm-table">
            <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Amount</th></tr></thead>
            <tbody>
              {grid.map((row, index) => (
                <tr key={index}>
                  <td><Input type="date" value={row.spent_on} onChange={(event) => setGrid((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, spent_on: event.target.value } : item))} /></td>
                  <td>
                    <Select value={row.category} onChange={(event) => setGrid((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, category: event.target.value } : item))}>
                      {categories.map((item) => <option key={item.id}>{item.name}</option>)}
                    </Select>
                  </td>
                  <td><Input value={row.notes ?? ""} onChange={(event) => setGrid((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, notes: event.target.value } : item))} /></td>
                  <td><Input value={String(row.amount ?? "")} onChange={(event) => setGrid((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, amount: event.target.value } : item))} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <Cluster gap={2}>
            <Button variant="secondary" onClick={() => setGrid((current) => [...current, emptyLine(defaultCategory, today)])}>+ Row</Button>
            <Button
              onClick={() => run(async () => {
                const lines = grid.filter((row) => Number(row.amount) > 0).map((row) => ({ ...row, amount: Number(row.amount) }));
                await workforceApi.addExpenseGrid(orgId, active.id, lines);
                setShowLine(false);
              }, "Grid could not be saved.", "Saved.")}
            >
              Save grid
            </Button>
          </Cluster>
        </Stack>
      )}
      {mode === "mileage" && (
        <Stack gap={3}>
          <Cluster gap={3}>
            <FormField label="Destination" htmlFor="m-dest"><Input id="m-dest" value={mileage.destination} onChange={(event) => setMileage({ ...mileage, destination: event.target.value })} /></FormField>
            <FormField label="Mileage (km)" htmlFor="m-km"><Input id="m-km" value={mileage.mileage_km} onChange={(event) => setMileage({ ...mileage, mileage_km: event.target.value })} /></FormField>
            <FormField label="Mileage rate" htmlFor="m-rate"><Input id="m-rate" value={mileage.mileage_rate} onChange={(event) => setMileage({ ...mileage, mileage_rate: event.target.value })} /></FormField>
          </Cluster>
          <Cluster gap={3}>
            <FormField label="Per-diem rate" htmlFor="m-pd"><Input id="m-pd" value={mileage.per_diem_rate} onChange={(event) => setMileage({ ...mileage, per_diem_rate: event.target.value })} /></FormField>
            <FormField label="Per-diem days" htmlFor="m-days"><Input id="m-days" value={mileage.per_diem_days} onChange={(event) => setMileage({ ...mileage, per_diem_days: event.target.value })} /></FormField>
            <div><span className="wf-muted">Estimated cost</span><div className="home-kpi-value">{mileageCost.toFixed(2)}</div></div>
          </Cluster>
          <FormField label="Comments" htmlFor="m-c"><Input id="m-c" value={mileage.comments} onChange={(event) => setMileage({ ...mileage, comments: event.target.value })} /></FormField>
          <Cluster gap={2}>
            <Button
              onClick={() => run(async () => {
                await workforceApi.addExpenseLine(orgId, active.id, {
                  spent_on: today,
                  category: "Mileage",
                  amount: mileageCost,
                  expense_type: "Mileage",
                  destination: mileage.destination,
                  mileage_km: Number(mileage.mileage_km) || 0,
                  mileage_rate: Number(mileage.mileage_rate) || 0,
                  per_diem_rate: Number(mileage.per_diem_rate) || 0,
                  per_diem_days: Number(mileage.per_diem_days) || 0,
                  comments: mileage.comments,
                  notes: mileage.destination,
                  employee_paid: true,
                });
                setMileage({ destination: "", per_diem_rate: "", per_diem_days: "", mileage_km: "", mileage_rate: settings?.default_mileage_rate ?? "", comments: "" });
                setShowLine(false);
              }, "Mileage could not be saved.", "Saved.")}
              disabled={mileageCost <= 0}
            >
              Add to report
            </Button>
            <Button variant="ghost" onClick={() => setMileage({ destination: "", per_diem_rate: "", per_diem_days: "", mileage_km: "", mileage_rate: settings?.default_mileage_rate ?? "", comments: "" })}>Clear</Button>
          </Cluster>
        </Stack>
      )}
    </Stack>
  ) : null;

  const editor = active ? (
    <Stack gap={4}>
      {headerFields}
      <Cluster gap={3}>
        {editable && (
          <Button onClick={() => run(() => workforceApi.patchExpense(orgId, active.id, {
            title, description, period_start: periodStart, period_end: periodEnd,
          }), "Report could not be saved.", "Saved.")} disabled={!title}>
            Save
          </Button>
        )}
        {editable && (
          <Button variant="secondary" onClick={() => {
            setShowLine((open) => !open);
            setEditingLineId(null);
            setMode("single");
            setLine(emptyLine(defaultCategory, today));
          }}>
            {showLine && !editingLineId ? "Close line form" : "+ New line item"}
          </Button>
        )}
        {editable && active.lines.length > 0 && (
          <Button onClick={() => run(async () => {
            await workforceApi.submitExpense(orgId, active.id);
            setActive(null);
          }, "Submit failed")}>
            {active.status === "rejected" ? "Resubmit" : "Submit"}
          </Button>
        )}
        {editable && (
          <Button variant="ghost" onClick={() => run(async () => {
            await workforceApi.deleteExpense(orgId, active.id);
            setActive(null);
          }, "Delete failed")}>Delete</Button>
        )}
        <Button variant="ghost" onClick={() => setActive(null)}>Close</Button>
      </Cluster>
      {active.decision_comment && <p>Rejection comments: {active.decision_comment}</p>}
      {lineCapture}
      <div>
        <h2 className="home-section-title">Transactions</h2>
        {active.lines.length === 0 ? (
          <EmptyState title="No line items" description="Add a single entry, a grid row, or mileage." />
        ) : (
          <table className="crm-table">
            <thead>
              <tr><th>Type</th><th>Transaction date</th><th>Category</th><th>Description</th><th>Comments</th><th>Amount</th><th>Refund</th><th>Receipt</th><th></th></tr>
            </thead>
            <tbody>
              {active.lines.map((item) => (
                <tr key={item.id}>
                  <td>{item.expense_type}</td>
                  <td>{formatDate(item.spent_on)}</td>
                  <td>{item.category}</td>
                  <td>{item.notes || "—"}</td>
                  <td>{item.comments || "—"}</td>
                  <td>{formatMoney(item.amount, item.currency || active.currency)}</td>
                  <td>{item.employee_paid ? "Yes" : "No"}</td>
                  <td>
                    {item.receipt_filename && item.id ? (
                      <Button variant="ghost" onClick={() => viewReceipt(active.id, item.id as string)}>{item.receipt_filename}</Button>
                    ) : (
                      item.receipt_key || (item.missing_receipt ? "Missing" : "—")
                    )}
                  </td>
                  <td>
                    {editable && item.id && (
                      <Cluster gap={2}>
                        <Button variant="ghost" onClick={() => {
                          setMode("single");
                          setShowLine(true);
                          setEditingLineId(item.id as string);
                          setLine({ ...item, spent_on: item.spent_on, amount: String(item.amount ?? "") });
                        }}>Edit</Button>
                        <Button variant="ghost" onClick={() => run(() => workforceApi.deleteExpenseLine(orgId, active.id, item.id as string), "Could not delete line.")}>Delete</Button>
                      </Cluster>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Stack>
  ) : null;

  return (
    <Stack gap={4}>
      <div className="crm-page-header">
        <div>
          <h1 className="home-title">Expenses and Claims</h1>
          <p className="home-subtitle">Drafts you can still edit, plus claims you have submitted or that have been approved.</p>
        </div>
        <Button onClick={startCreate}>New expense report</Button>
      </div>
      {error && <p className="crm-error">{error}</p>}
      {creating && (
        <Card>
          <Stack gap={3}>
            {headerFields}
            <Cluster gap={2}>
              <Button
                onClick={() => run(async () => {
                  const created = await workforceApi.createExpense(orgId, {
                    title, description, period_start: periodStart, period_end: periodEnd, currency: userCurrency,
                  });
                  setCreating(false);
                  setActive(created);
                  hydrateReport(created);
                }, "Report could not be saved.", "Saved.")}
                disabled={!title}
              >
                Create report
              </Button>
              <Button variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
            </Cluster>
          </Stack>
        </Card>
      )}
      <Cluster gap={3}>
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search reports or descriptions" aria-label="Search reports" />
      </Cluster>
      {drafts.length === 0 ? (
        <EmptyState title="No drafts" description="Use New expense report to start a draft. Submitted and approved claims are listed below." />
      ) : (
        <table className="crm-table">
          <thead>
            <tr>
              <th>Report</th>
              <th>Number</th>
              <th>Period</th>
              <th>Status</th>
              <th>Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {drafts.map((report) => (
              <Fragment key={report.id}>
                <tr
                  className={`wf-row-clickable${active?.id === report.id ? " wf-row-open" : ""}`}
                  onClick={() => openReport(report)}
                >
                  <td>{report.title}</td>
                  <td>{report.claim_number || "—"}</td>
                  <td>{formatDate(report.period_start)} → {formatDate(report.period_end)}</td>
                  <td>
                    <span className={statusBadge(report.status)}>{statusLabel(report.status)}</span>
                    {report.decision_comment ? <div>Rejection comment: {report.decision_comment}</div> : null}
                  </td>
                  <td>{formatMoney(report.total, report.currency)}</td>
                  <td>
                    <Cluster gap={2}>
                      <Link to={reportHref(report.id)} onClick={(event) => event.stopPropagation()}>Open</Link>
                      <span>{active?.id === report.id ? "Close" : "View / Edit"}</span>
                    </Cluster>
                  </td>
                </tr>
                {active?.id === report.id && (
                  <tr className="wf-row-detail">
                    <td colSpan={6} onClick={(event) => event.stopPropagation()}>
                      {editor}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
      <h2 className="home-section-title">Your claims</h2>
      <p className="home-subtitle">Submitted and approved reports stay here after they leave the draft list.</p>
      {claims.length === 0 ? (
        <EmptyState title="No submitted or approved claims" description="Submit a draft and it will appear here, including after it is approved." />
      ) : (
        <table className="crm-table">
          <thead>
            <tr>
              <th>Report</th>
              <th>Number</th>
              <th>Period</th>
              <th>Status</th>
              <th>Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {claims.map((report) => (
              <tr key={report.id}>
                <td>{report.title}</td>
                <td>{report.claim_number || "—"}</td>
                <td>{formatDate(report.period_start)} → {formatDate(report.period_end)}</td>
                <td><span className={statusBadge(report.status)}>{statusLabel(report.status)}</span></td>
                <td>{formatMoney(report.total, report.currency)}</td>
                <td><Link to={reportHref(report.id)}>Open</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Stack>
  );
}

function ExpenseDetail({ orgId }: { orgId: string }) {
  const { reportId } = useParams();
  const { formatMoney, formatDate } = useLocale();
  const [report, setReport] = useState<ExpenseReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [settled, setSettled] = useState(false);

  const load = useCallback(async () => {
    if (!reportId) return;
    setSettled(false);
    try {
      setReport(await workforceApi.getExpense(orgId, reportId));
    } catch (err) {
      setError(fail(err, "The expense report could not be loaded."));
    } finally {
      setSettled(true);
    }
  }, [orgId, reportId]);
  useEffect(() => { void load(); }, [load]);

  if (error) return <p className="crm-error">{error}</p>;
  if (!settled) return <p className="home-subtitle">Loading the expense report…</p>;
  if (!report) {
    return (
      <Stack gap={3}>
        <p className="crm-error">This expense claim could not be opened.</p>
        <Link to="../claims">Back to claims</Link>
      </Stack>
    );
  }
  const back = report.status === "draft" || report.status === "rejected" ? "../claims" : "../submitted";
  return (
    <Stack gap={4}>
      <div>
        <Link to={back}>Back to the list</Link>
        <h1 className="home-title">{report.title}</h1>
        <p className="home-subtitle">
          {report.claim_number || "No claim number"} · {report.user_name || "You"} · {formatDate(report.period_start)} → {formatDate(report.period_end)}
        </p>
      </div>
      <Card>
        <Stack gap={2}>
          <div className="home-kpi-label">Status</div>
          <span className={statusBadge(report.status)}>{statusLabel(report.status)}</span>
          <div className="home-kpi-label">Rejection comment</div>
          <p>{report.decision_comment?.trim() || "No rejection comment was recorded."}</p>
          <div className="home-kpi-label">Total</div>
          <p>{formatMoney(report.total, report.currency)}</p>
        </Stack>
      </Card>
      {report.lines.length === 0 ? (
        <EmptyState title="No lines" description="This report has no expense lines." />
      ) : (
        <table className="crm-table">
          <thead>
            <tr><th>Date</th><th>Category</th><th>Notes</th><th>Amount</th></tr>
          </thead>
          <tbody>
            {report.lines.map((line, index) => (
              <tr key={line.id ?? index}>
                <td>{formatDate(line.spent_on)}</td>
                <td>{line.category}</td>
                <td>{line.notes || "—"}</td>
                <td>{formatMoney(line.amount, line.currency || report.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Stack>
  );
}

function ExpenseSubmitted({ orgId, userId, reportHref }: { orgId: string; userId: string; reportHref: (id: string) => string }) {
  const [reports, setReports] = useState<ExpenseReport[]>([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<ExpenseReport | null>(null);
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await workforceApi.listExpenses(orgId);
      const next = result.reports.filter((report) => ["submitted", "approved", "rejected"].includes(report.status));
      setReports(next);
      setOpen((current) => (current ? next.find((report) => report.id === current.id) ?? null : null));
    } catch (err) {
      setError(fail(err, "Submitted reports could not be loaded."));
    }
  }, [orgId]);
  useEffect(() => { void load(); }, [load]);

  const filtered = reports.filter((report) => {
    const hay = `${report.title} ${report.description ?? ""} ${report.claim_number ?? ""} ${report.user_name ?? ""}`.toLowerCase();
    return hay.includes(search.toLowerCase());
  });
  const reimbursement = filtered.reduce((sum, report) => sum + asNumber(report.reimbursable_total), 0);

  async function run(action: () => Promise<unknown>, fallback: string) {
    setError(null);
    try {
      await action();
      setComment("");
      await load();
    } catch (err) {
      setError(fail(err, fallback));
    }
  }

  return (
    <Stack gap={4}>
      <div>
        <h1 className="home-title">Submitted reports</h1>
        <p className="home-subtitle">Managers approve or reject the whole report. Rejection comments are required to reject.</p>
      </div>
      {error && <p className="crm-error">{error}</p>}
      <Cluster gap={3}>
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search report, description or number" />
      </Cluster>
      {filtered.length === 0 ? (
        <EmptyState title="No submitted reports" description="Submitted claims appear here for review." />
      ) : (
        <table className="crm-table">
          <thead>
            <tr><th>Report</th><th>Submitted by</th><th>Period</th><th>Status</th><th>Total</th><th>Reimbursement</th></tr>
          </thead>
          <tbody>
            {filtered.map((report) => (
              <tr key={report.id} className="wf-row-clickable" onClick={() => setOpen(report)}>
                <td>
                  <Link to={reportHref(report.id)} onClick={(event) => event.stopPropagation()}>Open</Link>
                  <div>{report.title}</div>
                </td>
                <td>{report.user_name}</td>
                <td>{report.period_start} → {report.period_end}</td>
                <td>
                  <span className={statusBadge(report.status)}>{statusLabel(report.status)}</span>
                  {report.decision_comment ? <div>Rejection comment: {report.decision_comment}</div> : null}
                </td>
                <td>{report.total} {report.currency}</td>
                <td>{report.reimbursable_total} {report.currency}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><td colSpan={5}>Reimbursement</td><td>{reimbursement.toFixed(2)}</td></tr>
          </tfoot>
        </table>
      )}
      {open && (
        <Card>
          <Stack gap={3}>
            <h2 className="home-section-title">{open.title}</h2>
            <p className="wf-muted">{open.user_name} · {open.claim_number} · {open.lines.length} lines</p>
            {open.status === "submitted" && open.user_id === userId && (
              <>
                <p className="wf-muted">You cannot approve your own expense report. A manager must decide this.</p>
                <Cluster gap={2}>
                  <Button
                    variant="secondary"
                    onClick={() => run(async () => {
                      await workforceApi.withdrawExpense(orgId, open.id);
                      setOpen(null);
                    }, "The report could not be withdrawn.")}
                  >
                    Withdraw report
                  </Button>
                </Cluster>
                <p className="wf-muted">Withdrawing returns it to your unsubmitted drafts, where you can edit or delete it.</p>
              </>
            )}
            {open.status === "submitted" && open.user_id !== userId && (
              <>
                <FormField label="Rejection comments (required to reject)" htmlFor="ex-rej">
                  <Input id="ex-rej" value={comment} onChange={(event) => setComment(event.target.value)} />
                </FormField>
                <Cluster gap={2}>
                  <Button onClick={() => run(() => workforceApi.approveExpense(orgId, open.id), "Approve failed")}>Approve report</Button>
                  <Button variant="ghost" onClick={() => run(() => workforceApi.rejectExpense(orgId, open.id, comment), "Reject failed")}>Reject report</Button>
                </Cluster>
              </>
            )}
            {open.decision_comment && <p>{open.decision_comment}</p>}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

function ExpenseCategories({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const { can } = useAccess();
  const canConfigure = can("expense.reports", "configure");
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [name, setName] = useState("");
  const [gl, setGl] = useState("");
  const [limit, setLimit] = useState("");
  const [receipt, setReceipt] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setCategories((await workforceApi.listCategories(orgId, true)).categories);
    } catch (err) {
      setError(fail(err, "Categories could not be loaded."));
    }
  }, [orgId]);
  useEffect(() => { void load(); }, [load]);

  return (
    <Stack gap={4}>
      <div>
        <h1 className="home-title">Categories</h1>
        <p className="home-subtitle">Limits and receipt rules feed the line-item form. Create the standard set if this org is empty.</p>
      </div>
      {error && <p className="crm-error">{error}</p>}
      {!canConfigure && <p className="wf-muted">You need the Expense app admin role to manage categories.</p>}
      <Card>
        <Cluster gap={3}>
          <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Name" disabled={!canConfigure} aria-label="Category name" />
          <Input value={gl} onChange={(event) => setGl(event.target.value)} placeholder="GL code" disabled={!canConfigure} aria-label="GL code" />
          <Input value={limit} onChange={(event) => setLimit(event.target.value)} placeholder="Limit" disabled={!canConfigure} aria-label="Limit" />
          <Checkbox label="Receipt required" checked={receipt} onChange={(event) => setReceipt(event.target.checked)} disabled={!canConfigure} />
          <Button
            disabled={!canConfigure || !name}
            onClick={() => {
              setError(null);
              void workforceApi.createCategory(orgId, { name, gl_code: gl || undefined, amount_limit: limit ? Number(limit) : undefined, receipt_required: receipt })
                .then(() => {
                  setName("");
                  setGl("");
                  setLimit("");
                  setReceipt(false);
                  reportSaved(show, "Category saved.");
                  return load();
                })
                .catch((err) => setError(reportSaveFailure(show, err, "Category could not be saved.")));
            }}
          >
            + Add
          </Button>
          <Button
            variant="secondary"
            disabled={!canConfigure}
            onClick={() => workforceApi.seedCategories(orgId).then(load).catch((err) => setError(fail(err, "Create failed")))}
          >
            Create standard categories
          </Button>
        </Cluster>
      </Card>
      <table className="crm-table">
        <thead>
          <tr><th>Name</th><th>GL code</th><th>Limit</th><th>Receipt</th><th>State</th><th></th></tr>
        </thead>
        <tbody>
          {categories.map((item) => (
            <tr key={item.id}>
              <td>{item.name}</td>
              <td>{item.gl_code || "—"}</td>
              <td>{item.amount_limit || "—"}</td>
              <td>{item.receipt_required ? "Required" : "Optional"}</td>
              <td><span className={item.active ? "crm-badge crm-badge--active" : "crm-badge"}>{item.active ? "Active" : "Inactive"}</span></td>
              <td>
                <Button
                  variant="ghost"
                  disabled={!canConfigure}
                  onClick={() => workforceApi.patchCategory(orgId, item.id, { active: !item.active }).then(load).catch((err) => setError(fail(err, "Could not update category.")))}
                >
                  {item.active ? "Deactivate" : "Activate"}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Stack>
  );
}

function ExpenseSettingsPage({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const { can } = useAccess();
  const { currency, timezone } = useLocale();
  const canConfigure = can("expense.reports", "configure");
  const [rate, setRate] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void workforceApi.getSettings(orgId).then((settings) => {
      setRate(settings.default_mileage_rate ?? "");
    }).catch((err) => setError(fail(err, "Settings could not be loaded.")));
  }, [orgId]);

  return (
    <Stack gap={4}>
      <div>
        <h1 className="home-title">Settings</h1>
        <p className="home-subtitle">Default mileage rate for the calculator. Currency and timezone come from User settings.</p>
      </div>
      {error && <p className="crm-error">{error}</p>}
      {!canConfigure && <p className="wf-muted">You need the Expense app admin role to change settings.</p>}
      <Card>
        <Stack gap={3}>
          <p className="wf-muted">New reports use {currency} and {timezone} from your user settings.</p>
          <FormField label="Default mileage rate" htmlFor="ex-rate"><Input id="ex-rate" value={rate} onChange={(event) => setRate(event.target.value)} disabled={!canConfigure} /></FormField>
          <Button
            disabled={!canConfigure}
            onClick={() => {
              setError(null);
              setSaved(false);
              void workforceApi.putSettings(orgId, { default_mileage_rate: rate || null })
                .then(() => {
                  setSaved(true);
                  reportSaved(show);
                })
                .catch((err) => setError(reportSaveFailure(show, err, "Settings could not be saved.")));
            }}
          >
            Save
          </Button>
          {saved && <p className="wf-muted">Saved.</p>}
        </Stack>
      </Card>
    </Stack>
  );
}
